import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, from, map } from 'rxjs';
import { collection, onSnapshot, orderBy, query, QuerySnapshot, DocumentData } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';

import { FirebaseService } from './firebase.service';

export interface MembershipReview {
  id: string;
  userId: string;
  membershipId: string;
  email: string;
  provider: string;
  profile?: any;
  membershipData?: any;
  pdfUrl: string;
  storagePath?: string;
  reviewType?: 'soft' | 'hard';
  accessGrantedDuringReview?: boolean;
  status?: string;
  missingSignals: string[];
  matchedSignals: string[];
  fileInfo: string;
  uploadedAt?: Date | null;
  createdAt?: Date | null;
  updatedAt?: Date | null;
}

export interface MembershipDecision extends MembershipReview {
  decision: 'approved' | 'declined' | 'hard_required';
  reason?: string | null;
  decidedAt: Date | null;
  decidedBy?: string | null;
}

@Injectable({ providedIn: 'root' })
export class AdminMembershipReviewService implements OnDestroy {
  private pendingReviews$ = new BehaviorSubject<MembershipReview[]>([]);
  private decisions$ = new BehaviorSubject<MembershipDecision[]>([]);

  private storage = getStorage(this.firebaseService.app);
  private functions = getFunctions();
  private pendingUnsubscribe?: () => void;
  private decisionsUnsubscribe?: () => void;

  constructor(private firebaseService: FirebaseService) {
    const pendingRef = collection(this.firebaseService.firestore, 'pending-membership-verifications');
    const pendingQuery = query(pendingRef, orderBy('createdAt', 'desc'));
    this.pendingUnsubscribe = onSnapshot(pendingQuery, (snapshot) => {
      this.handlePendingSnapshot(snapshot).catch(err => console.error('Failed to process pending reviews', err));
    });

    const decisionsRef = collection(this.firebaseService.firestore, 'membership-verification-decisions');
    const decisionsQuery = query(decisionsRef, orderBy('decidedAt', 'desc'));
    this.decisionsUnsubscribe = onSnapshot(decisionsQuery, (snapshot) => {
      this.handleDecisionsSnapshot(snapshot).catch(err => console.error('Failed to process membership decisions', err));
    });
  }

  ngOnDestroy(): void {
    this.pendingUnsubscribe?.();
    this.decisionsUnsubscribe?.();
  }

  getPendingReviews(): Observable<MembershipReview[]> {
    return this.pendingReviews$.asObservable();
  }

  getDecisions(): Observable<MembershipDecision[]> {
    return this.decisions$.asObservable();
  }

  approveMembership(reviewId: string): Observable<void> {
    const callable = httpsCallable(this.functions, 'approveMembershipVerification');
    return from(callable({ reviewId })).pipe(map(() => void 0));
  }

  declineMembership(reviewId: string, reason: string): Observable<void> {
    const callable = httpsCallable(this.functions, 'declineMembershipVerification');
    return from(callable({ reviewId, reason })).pipe(map(() => void 0));
  }

  requireHardVerification(reviewId: string, reason: string): Observable<void> {
    const callable = httpsCallable(this.functions, 'requireHardMembershipVerification');
    return from(callable({ reviewId, reason })).pipe(map(() => void 0));
  }

  private async handlePendingSnapshot(snapshot: QuerySnapshot<DocumentData>) {
    const reviews = await Promise.all(snapshot.docs.map(async (docSnap) => {
      const data = docSnap.data();
      const pdfUrl = await this.buildDownloadUrl(data?.storagePath);
      return this.buildReviewFromData(docSnap.id, data, pdfUrl);
    }));
    this.pendingReviews$.next(reviews);
  }

  private async handleDecisionsSnapshot(snapshot: QuerySnapshot<DocumentData>) {
    const decisions = snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      const base = this.buildReviewFromData(docSnap.id, data, '');
      return {
        ...base,
        decision: data?.decision ?? 'approved',
        reason: data?.reason ?? null,
        decidedAt: this.toDate(data?.decidedAt),
        decidedBy: data?.decidedBy ?? null,
      } as MembershipDecision;
    });
    this.decisions$.next(decisions);
  }

  private async buildDownloadUrl(path?: string | null): Promise<string> {
    if (!path) return '';
    try {
      const storageRef = ref(this.storage, path);
      return await getDownloadURL(storageRef);
    } catch (err) {
      console.error('Failed to generate download URL', err);
      return '';
    }
  }

  private buildReviewFromData(id: string, data: any, pdfUrl: string): MembershipReview {
    const flags = Array.isArray(data?.result?.flags) ? data.result.flags : [];
    const matchedSignals = Array.isArray(data?.result?.matchedSignals) ? data.result.matchedSignals : [];
    return {
      id,
      createdAt: data.createdAt ?? '',
      updatedAt: data.updatedAt ?? '',
      userId: data?.userId ?? '',
      membershipId: data?.membershipId ?? '',
      email: data?.email ?? '',
      provider: data?.provider ?? '',
      profile: data?.profile ?? null,
      membershipData: data?.membershipData ?? null,
      pdfUrl,
      storagePath: data?.storagePath ?? '',
      reviewType: data?.reviewType ?? 'hard',
      accessGrantedDuringReview: data?.accessGrantedDuringReview === true,
      status: data?.status ?? data?.result?.status ?? '',
      missingSignals: this.formatMissingSignals(flags),
      matchedSignals,
      fileInfo: this.formatFileInfo(data?.file),
      uploadedAt: this.toDate(data?.file?.uploadedAt ?? data?.createdAt),
    };
  }

  private formatMissingSignals(flags: any): string[] {
    if (!Array.isArray(flags)) return [];
    return flags.map((flag: any) => (typeof flag === 'string' ? this.capitalize(flag) : flag));
  }

  private formatFileInfo(file: any): string {
    if (!file) return 'Document';
    const name = file.name ?? 'Document';
    const size = typeof file.size === 'number' ? this.formatBytes(file.size) : null;
    return size ? `${name} (${size})` : name;
  }

  private formatBytes(size: number): string {
    if (!size) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const idx = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
    const value = size / Math.pow(1024, idx);
    return `${value.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
  }

  private capitalize(text: string): string {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  private toDate(value: any): Date | null {
    if (!value) return null;
    if (typeof value.toDate === 'function') {
      return value.toDate();
    }
    return value instanceof Date ? value : null;
  }
}
